<template>
  <div
    class="sg-collapse"
    :class="{ 'is-bordered': border}"
    :style="style"
  >
    <slot></slot>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, provide, computed } from 'vue'
import { UPDATE_MODEL_EVENT } from '@sgui-plus/utils/constants'
import { isNumber } from '@sgui-plus/utils/util'
import { CollapseContextKey } from './collapse.type'
import type { PropType, CSSProperties } from 'vue'
import type { CollapseProvider } from './collapse.type'

export default defineComponent({
  name: 'SgCollapse',
  props: {
    accordion: Boolean,
    modelValue: {
      type: [Array, String, Number] as PropType<
        string | number | Array<string | number>
      >,
      default: () => [],
    },
    width: [String, Number],
    border: {
      type: Boolean,
      default: true
    },
    arrowPosition: {
      type: String,
      default: 'right'
    },
    lineStyle: {
      type: [Boolean, Object],
      default: true
    },
    headerStyle: {
      type: Object,
      default: () => {}
    },
    contentStyle: {
      type: Object,
      default: () => {}
    },
  },
  emits: [UPDATE_MODEL_EVENT, 'on-change'],
  setup(props, { emit }) {
    const activeNames = ref([].concat(props.modelValue as never[]))

    const setActiveNames = (_activeNames) => {
      activeNames.value = [].concat(_activeNames)
      const value = props.accordion ? activeNames.value[0] : activeNames.value
      emit(UPDATE_MODEL_EVENT, value)
      emit('on-change', value)
    }

    const handleItemClick = (name: string | number) => {
      if (props.accordion) {
        setActiveNames(
          (activeNames.value[0] || activeNames.value[0] === 0) &&
            activeNames.value[0] === name
            ? ''
            : name
        )
      } else {
        const _activeNames = activeNames.value.slice(0)
        const index = _activeNames.indexOf(name as never)

        if (index > -1) {
          _activeNames.splice(index, 1)
        } else {
          _activeNames.push(name as never)
        }
        setActiveNames(_activeNames)
      }
    }

    const normalizeWidth = computed(() =>
      isNumber(props.width) ? `${props.width}px` : props.width
    )

    const style = computed<CSSProperties>(() => {
      const style: CSSProperties = {}
      const varPrefix = `--sg-collapse`

      if (props.width) {
        style[`${varPrefix}-width`] = normalizeWidth.value
      }

      return style
    })

    watch(
      () => props.modelValue,
      () => {
        activeNames.value = [].concat(props.modelValue as never[])
      },
      {
        deep: true,
      }
    )

    provide<CollapseProvider>(CollapseContextKey, {
      activeNames,
      handleItemClick,
      lineStyle: props.lineStyle,
      headerStyle: props.headerStyle,
      contentStyle: props.contentStyle,
      arrowPosition: props.arrowPosition
    })

    return {
      activeNames,
      setActiveNames,
      handleItemClick,
      style
    }
  },
})
</script>
